/**
 * Platform-admin-only ID display row.
 *
 * Renders question_id (and optionally answer_id) in muted monospace text
 * with an optional click-to-copy button. ALWAYS renders nothing when the
 * current user is not a platform_admin / super_admin — every caller can
 * therefore drop the component in unconditionally without role checks at
 * the call site.
 *
 * The "answer_id" surfaced here is a deterministic synthetic identifier
 * `ans:<response_id>:<question_id>`. Per-answer UUIDs do not exist in the
 * survey_responses schema (answers are values keyed by question_id), so
 * this synthetic form gives the platform admin a stable, copy-pasteable
 * address for any specific answer cell in any specific response — useful
 * for support tickets, audit-trail tracing, and comparison-survey mapping.
 * Nothing is written to the DB; this is read-only display only.
 */
import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const ADMIN_ROLES = new Set(['platform_admin', 'super_admin']);

export function buildAnswerId(responseId, questionId) {
  if (!responseId || !questionId) return '';
  return `ans:${responseId}:${questionId}`;
}

/**
 * Platform-admin-only "Edited by {email} · {date}" meta line. Bundled
 * alongside the audit-trail IDs so the two always share the same role
 * gate. Returns null for any non-admin role — safe to drop in
 * unconditionally.
 */
export function AdminEditMeta({ editedBy, editedAt, fmt, className = '', testId }) {
  const { user } = useAuth();
  if (!user || !ADMIN_ROLES.has(user.role)) return null;
  if (!editedBy) return null;
  const when = fmt && editedAt ? fmt(editedAt) : (editedAt || '');
  return (
    <p
      className={`text-[10px] text-[#64748B] pl-5 ${className}`}
      data-testid={testId}
    >
      Edited by {editedBy}{when ? ` · ${when}` : ''}
    </p>
  );
}

export default function AdminIdRow({
  questionId,
  answerId,
  responseId,    // optional — when provided, an answer_id is auto-built
  showCopy = false,
  showLabel = false,  // when true, prefix with "QUESTION ID" label and use larger style
  className = '',
  testId,
}) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  if (!user || !ADMIN_ROLES.has(user.role)) return null;
  if (!questionId) return null;

  const resolvedAnswerId =
    answerId || (responseId ? buildAnswerId(responseId, questionId) : null);

  const copyText = resolvedAnswerId
    ? `${questionId}  ${resolvedAnswerId}`
    : questionId;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silently ignore */
    }
  };

  if (showLabel) {
    // Settings-panel layout: stacked, labelled, with copy button.
    return (
      <div
        className={`pt-4 mt-4 border-t border-dashed border-[#E2E8F0] ${className}`}
        data-testid={testId || 'admin-id-row'}
      >
        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8] mb-1">
          Question ID
        </p>
        <div className="flex items-center gap-2">
          <code
            className="flex-1 text-xs font-mono text-[#475569] bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1 truncate"
            data-testid={testId ? `${testId}-qid` : 'admin-id-row-qid'}
            title={questionId}
          >
            {questionId}
          </code>
          <button
            type="button"
            onClick={onCopy}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#475569]"
            data-testid={testId ? `${testId}-copy` : 'admin-id-row-copy'}
          >
            {copied
              ? <><Check className="w-3 h-3 text-emerald-600" /> Copied</>
              : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
        </div>
      </div>
    );
  }

  // Compact inline layout (question list / QA review / response detail).
  return (
    <p
      className={`text-[10px] font-mono text-[#94A3B8] leading-tight ${className}`}
      data-testid={testId || 'admin-id-row-inline'}
    >
      <span data-testid={testId ? `${testId}-qid` : undefined}>{questionId}</span>
      {resolvedAnswerId && (
        <>
          <span className="mx-1.5 text-[#CBD5E1]">·</span>
          <span data-testid={testId ? `${testId}-ans` : undefined}>{resolvedAnswerId}</span>
        </>
      )}
      {showCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="ml-2 align-middle inline-flex items-center gap-0.5 text-[10px] text-[#64748B] hover:text-[#1A3E6F]"
          data-testid={testId ? `${testId}-copy` : undefined}
        >
          {copied
            ? <><Check className="w-2.5 h-2.5 text-emerald-600" /> Copied</>
            : <><Copy className="w-2.5 h-2.5" /></>}
        </button>
      )}
    </p>
  );
}
